import React, { useState } from "react";
import { formatDisplayDate, getLocalISODate } from "../utils";
import { DesktopIcon, MonitorIcon } from "../../admin/components/Shared";

const ChevronLeft = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m15 18-6-6 6-6" />
  </svg>
);

const ChevronRight = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m9 18 6-6-6-6" />
  </svg>
);

const RecurringIcon = () => (
  <svg
    className="w-3 h-3 mr-1"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
    <path d="M21 3v5h-5" />
  </svg>
);

const MyBookings = ({
  myBookings,
  sections,
  onCancel,
  onCancelAllRecurring,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const today = getLocalISODate(new Date());

  const sorted = [...myBookings].sort((a, b) => {
    const dateA = a.date === "permanent" ? a.startTime : a.date;
    const dateB = b.date === "permanent" ? b.startTime : b.date;
    if (!dateA) return 1;
    if (!dateB) return -1;
    return dateA.localeCompare(dateB);
  });



  const totalPages = Math.ceil(sorted.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const visibleBookings = sorted.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="mb-10 rounded-[24px] bg-white overflow-hidden shadow-2xl border border-slate-100">
      {/* Header Section */}
      <div className="bg-[#e8e5dc] px-6 py-4 md:py-5 relative overflow-hidden rounded-t-[24px] sticky top-0 z-20 lg:relative lg:top-auto lg:z-auto border-b border-[#d8d5cc]">
        <div className="relative z-50 flex items-center justify-between gap-3">
          <h2 className="text-xl font-black text-[#1a1a1a] tracking-tight">
            Your bookings
          </h2>

          <span className="flex h-7 sm:h-8 items-center gap-2 rounded-full bg-[#1a1a1a] px-3 lg:px-5 text-[10px] sm:text-xs font-black text-white uppercase tracking-widest whitespace-nowrap">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.7)]" />
            {myBookings.length} ACTIVE
          </span>
        </div>
        <p className="text-sm text-[#7c7a72] mt-2 font-light tracking-wide">
          Review and manage your active seat reservations
        </p>
      </div>

      <div className="lg:p-10 lg:pb-5">
        {myBookings.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50 p-16 text-center">
            <p className="text-md lg:text-lg font-medium text-slate-400">
              You have no active bookings at the moment.
            </p>

            <p className="mt-1 text-sm text-slate-400">
              Select a seat below to get started.
            </p>
          </div>
        ) : (
          <div className="space-y-0">
            {/* Table Header */}
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 pb-6 text-[12px] font-bold uppercase tracking-widest text-[#8a8a82] border-b border-[#e5e3dc]">
              <div className="col-span-2">BOOKING TYPE</div>
              <div className="col-span-2">SEAT</div>
              <div className="col-span-2">SECTION</div>
              <div className="col-span-2">WORKSTATION</div>
              <div className="col-span-2">DATE</div>
              <div className="col-span-2 text-right pr-4">ACTION</div>
            </div>

            {/* Table Rows */}
            {visibleBookings.map((booking) => {
              const section = sections.find((s) => s.id === booking.sectionId);

              const seat = section?.seats.find((s) => s.id === booking.seatId);

              const seatLabel = booking.seatNumber || seat?.label || "—";

              const isPermanent = booking.date === "permanent";

              const isRecurring = booking.bookingType === "recurring";

              return (
                <div
                  key={booking.id}
                  className="grid grid-cols-2 md:grid-cols-12 gap-4 px-4 md:px-6 py-5 border-b border-[#e5e3dc] last:border-0 hover:bg-slate-50/50 transition-colors last:rounded-b-[15px] lg:last:rounded-b-none items-center"
                >
                  {/* Booking Type */}
                  <div className="col-span-1 md:col-span-2 order-2 md:order-1 flex justify-end md:justify-start">
                    {isPermanent ? (
                      <span className="inline-flex items-center rounded-full bg-[#1a1a18] px-4 py-1.5 text-[9px] font-black uppercase tracking-widest text-[#ffffff]">
                        PERMANENT
                      </span>
                    ) : isRecurring ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-[#e8e8ff] px-4 py-1.5 text-[9px] font-black uppercase tracking-widest text-[#6c6cff]">
                        <RecurringIcon />
                        RECURRING
                      </span>
                    ) : booking.bookingType === "date-range" ? (
                      <span className="inline-flex items-center rounded-full bg-teal-100 px-4 py-1.5 text-[9px] font-black uppercase tracking-widest text-teal-700">
                        DATE-RANGE
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-slate-100 px-4 py-1.5 text-[9px] font-black uppercase tracking-widest text-slate-500">
                        ONE-TIME
                      </span>
                    )}
                  </div>

                  {/* Seat */}
                  <div className="col-span-1 md:col-span-2 order-1 md:order-2">
                    <p className="text-sm font-bold text-[#1a1a18]">
                      Seat {seatLabel}
                    </p>
                  </div>

                  {/* Section */}
                  <div className="col-span-1 md:col-span-2 order-3">
                    <p className="text-sm text-slate-500 truncate">
                      {section?.name || "Unknown Section"}
                    </p>
                  </div>

                  {/* Device Type */}
                  <div className="col-span-1 md:col-span-2 order-4">
                    <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider ${
                      (booking.seatType || seat?.type || "laptop").toLowerCase() === "monitor"
                        ? "bg-blue-50 text-blue-700"
                        : (booking.seatType || seat?.type || "laptop").toLowerCase() === "desktop"
                        ? "bg-emerald-50 text-emerald-700"
                        : "bg-amber-50 text-amber-700"
                    }`}>
                      {(booking.seatType || seat?.type || "laptop").toLowerCase() === "monitor" ? (
                        <MonitorIcon className="w-3 h-3" />
                      ) : (booking.seatType || seat?.type || "laptop").toLowerCase() === "desktop" ? (
                        <DesktopIcon className="w-3 h-3" />
                      ) : (
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><rect x="2" y="4" width="20" height="14" rx="2" /><path d="M2 18h20" /></svg>
                      )}
                      {booking.seatType || seat?.type || "laptop"}
                    </span>
                  </div>

                  {/* Date / Status */}
                  <div className="col-span-1 md:col-span-2 order-5">
                    <p className="text-sm text-slate-600 font-medium">
                      {formatDisplayDate(
                        isPermanent
                          ? (booking.startTime?.slice(0, 10) || booking.createdAt?.slice(0, 10) || "permanent")
                          : booking.date
                      )}
                    </p>
                  </div>

                  {/* Cancel Button */}
                  <div className="col-span-1 md:col-span-2 order-6 flex justify-end">
                    <button
                      type="button"
                      onClick={() => onCancel(booking.id)}
                      className="px-6 py-2 text-xs font-bold text-[#f43f5e] bg-[#fff1f2] rounded-xl hover:bg-[#ffe4e6] transition-all active:scale-95 border border-[#fecaca]/30"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination Section */}
        {totalPages > 1 && (
          <div className="mt-10 flex flex-col md:flex-row items-center justify-between gap-6 mb-10">
            <div className="text-sm font-medium text-slate-400">
              Showing {startIndex + 1}-
              {Math.min(startIndex + itemsPerPage, sorted.length)} of{" "}
              {sorted.length}
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => prev - 1)}
                className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50 disabled:opacity-30 shadow-sm"
              >
                <ChevronLeft />
              </button>

              {[...Array(totalPages)].map((_, i) => {
                const pageNum = i + 1;

                if (
                  totalPages > 5 &&
                  Math.abs(pageNum - currentPage) > 1 &&
                  pageNum !== 1 &&
                  pageNum !== totalPages
                ) {
                  if (pageNum === 2 || pageNum === totalPages - 1) {
                    return (
                      <span key={pageNum} className="px-1 text-slate-400">
                        ...
                      </span>
                    );
                  }

                  return null;
                }

                return (
                  <button
                    key={pageNum}
                    type="button"
                    onClick={() => setCurrentPage(pageNum)}
                    className={`h-10 w-10 rounded-xl text-sm font-bold transition-all ${currentPage === pageNum
                      ? "bg-[#1a1a18] text-white shadow-lg scale-105"
                      : "text-slate-500 hover:bg-slate-50"
                      }`}
                  >
                    {pageNum}
                  </button>
                );
              })}

              <button
                type="button"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((prev) => prev + 1)}
                className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50 disabled:opacity-30 shadow-sm"
              >
                <ChevronRight />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyBookings;
